import { labelOfPredicateKey, type IdeaHypothesis, type IdeaPredicate, type PooledCell } from "./ideaPool";

/**
 * W5 OWNERIN (B1, B16): the reverse-grill. When an owner's idea is contradicted
 * by the pooled data, the system asks a question. It never overrides the owner
 * and it never obeys into losses: the text is a template over computed
 * figures, and it ends on a statement that nothing changed.
 *
 * Trigger: the idea is the owner's (an edit signal has no claim to contradict),
 * it states a direction, both sides have `REVERSE_GRILL_MIN_N` or more threads,
 * and the 95% interval on the won-rate gap lies wholly on the other side of
 * zero. An interval that touches zero is silence.
 */

/**
 * Threads per side before a contradiction is worth saying. This is W5's own
 * constant, not a copy of F-15's threads-per-pattern floor and not a ruling
 * about it: it only gates a question put to the owner. AWAITING RATIFICATION.
 */
export const REVERSE_GRILL_MIN_N = 5;

export interface ReverseGrill {
  readonly kind: "contradiction";
  /** The first owner note behind the idea. */
  readonly noteId: string;
  readonly predicate: IdeaPredicate;
  readonly direction: "up" | "down";
  readonly withN: number;
  readonly withWon: number;
  readonly withoutN: number;
  readonly withoutWon: number;
  readonly lowPp: number;
  readonly highPp: number;
}

const wholeCount = (n: number): boolean => Number.isInteger(n) && n >= 0;

export function reverseGrill(h: IdeaHypothesis, pooled: PooledCell): ReverseGrill | null {
  if (h.source !== "owner_note" || h.direction === null) return null;
  const noteId = h.sourceIds[0];
  if (noteId === undefined) return null;
  const { withN, withWon, withoutN, withoutWon, wilson95Pp } = pooled;
  if (![withN, withWon, withoutN, withoutWon].every(wholeCount)) return null;
  if (withWon > withN || withoutWon > withoutN) return null;
  if (withN < REVERSE_GRILL_MIN_N || withoutN < REVERSE_GRILL_MIN_N) return null;
  if (wilson95Pp === null) return null;
  const [lowPp, highPp] = wilson95Pp;
  if (!Number.isFinite(lowPp) || !Number.isFinite(highPp) || lowPp > highPp) return null;
  const contradicted = h.direction === "up" ? highPp < 0 : lowPp > 0;
  if (!contradicted) return null;
  return { kind: "contradiction", noteId, predicate: h.predicate, direction: h.direction, withN, withWon, withoutN, withoutWon, lowPp, highPp };
}

const pp = (n: number): string => n.toFixed(1);

/** The owner-facing text: computed figures in a fixed template. */
export function renderReverseGrill(g: ReverseGrill): string {
  const claim = g.direction === "up" ? "helps" : "hurts";
  return [
    `You said ${labelOfPredicateKey(g.predicate.key)} ${claim}. The threads so far read the other way: ` +
      `with it ${g.withWon} of ${g.withN} won, without it ${g.withoutWon} of ${g.withoutN}, ` +
      `a gap of ${pp(g.lowPp)} to ${pp(g.highPp)} points (95% interval).`,
    "What would you count to test this? Nothing has been changed, and nothing will be until you answer.",
  ].join(" ");
}

/** The question for an idea the catalog cannot count. */
export function unmappableQuestion(): string {
  return "I could not turn this into something I can count. What would I count to test it? Nothing has been changed.";
}
